(() => {
  const callback = () => {
    if (!window.themeComponents.snippets.register('size-guide-ds')) return;

    const { locale, settings = {} } = window.themeComponents.snippets['size-guide-ds'] ?? {};

    // Ranges are typed by hand in the metaobject, so they come as "63 - 67" or "63-67".
    // Everything that reads a range goes through these two helpers instead of matching ' - '.
    const RANGE_SPLIT = /\s*-\s*/;
    const isRangeValue = (value) => /^\s*\d+(?:[.,]\d+)?\s*-\s*\d+(?:[.,]\d+)?\s*$/.test(String(value ?? ''));
    const splitRange = (value) => String(value ?? '').split(RANGE_SPLIT);

    const convertCmToInch = ({ value }) => {
      if (value === '') return value;

      const separator = String(value).includes(' - ') ? ' - ' : '-'; // keep the chart's own style
      return splitRange(value)
        .map((cm) => (!isNaN(cm) ? (cm / 2.54).toFixed(1) : cm))
        .join(separator);
    };

    const disableBodyScroll = () => {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    };

    const enableBodyScroll = () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };

    // Complex (bralette) chrome: Band Size vertical bar, Cup Size bar, intro corner.
    // axis labels become part of the table so they align with / scroll with the columns.
    const createComplexTable = ({ selectedChart, unit }) => {
      const { tableValues, xAxisLabel = '', yAxisLabel = '', unitLabel = '' } = selectedChart ?? {};
      if (!tableValues || tableValues.length === 0) return '';

      const [headings, ...rows] = tableValues;
      // Column count comes from the widest data row, not from the heading row: a stray trailing
      // separator in the headings used to add a phantom cup column that no row filled, leaving a
      // blank strip down the right of the table. Short rows are padded so the zebra stays solid.
      const colCount = Math.max(...rows.map((row) => row.length), 0);
      const cupCount = Math.max(colCount - 2, 0); // first two data columns are info

      // Class per logical column → CSS colours by class (robust to the extra band column).
      // The two info columns get distinct suffixes so the mobile sticky offsets can target each.
      const colClass = (i) => {
        if (i === 0) return 'size-guide__cell--info size-guide__cell--info-1';
        if (i === 1) return 'size-guide__cell--info size-guide__cell--info-2';
        return (i - 2) % 2 === 0 ? 'size-guide__cell--cup-a' : 'size-guide__cell--cup-b';
      };

      const renderCells = ({ cells, tag }) =>
        cells
          .map((cell, i) => {
            const [visible, hidden] = String(cell).split('::');
            const display = unit === 'inch' ? convertCmToInch({ value: visible }) : visible;
            const cls = colClass(i);
            // Design fills gaps in the grid with a muted em dash instead of leaving them blank.
            if (!String(display).trim()) return `<${tag} class="${cls} size-guide__cell--empty">&mdash;</${tag}>`;
            if (!hidden) return `<${tag} class="${cls}">${display}</${tag}>`;
            return `<${tag} class="${cls}" data-size="${hidden}" tabindex="0" aria-label="${display}, ${hidden}">${display}</${tag}>`;
          })
          .join('');

      // Cup letter header cells only — the info-column headers are absorbed into the corner.
      // Sliced to the real column count so extra/blank headings can't outnumber the data.
      const cupLetters = Array.from({ length: cupCount }, (_, j) => headings[j + 2] ?? '')
        .map((cell, j) => `<th class="${j % 2 === 0 ? 'size-guide__cell--cup-a' : 'size-guide__cell--cup-b'}">${cell}</th>`)
        .join('');

      // Design has no vertical Band bar — BAND / CM are plain horizontal header columns.
      // Rows are padded to colCount so a short row renders an em-dash cell instead of a hole.
      const bodyRows = rows
        .map((row) => {
          const cells = Array.from({ length: colCount }, (_, i) => row[i] ?? '');
          return `<tr>${renderCells({ cells, tag: 'td' })}</tr>`;
        })
        .join('');

      // Footnote = two separate elements (design): the unit line + the cup legend.
      //  - unit line: the chart's own `footnote` field if set (so it switches with the chart
      //    toggle, e.g. EU/JP→cm, UK/US→inches), else the auto cm/inch locale line.
      //  - legend: the Customize "footnote — cup key" (same for cm & inch).
      // Footnote comes ONLY from the chart's own fields → it switches with the chart and shows
      // nothing until the client fills them (no locale / Customize fallback).
      const unitLine = selectedChart?.footnote || '';
      const legend = selectedChart?.footnoteLegend || '';
      const footnoteHtml =
        unitLine || legend
          ? `<p class="size-guide__table-footnote">${unitLine ? `<span class="size-guide__table-footnote-unit">${unitLine}</span>` : ''}${legend ? `<span class="size-guide__table-footnote-legend">${legend}</span>` : ''}</p>`
          : '';

      return `
        <div class="size-guide__table-outer size-guide__table-outer--complex">
          <div class="size-guide__table-inner">
            <table>
              <thead>
                <tr class="size-guide__chrome-row">
                  <th class="size-guide__col-head size-guide__cell--info-1" rowspan="2">${headings[0] || yAxisLabel || ''}</th>
                  <th class="size-guide__col-head size-guide__cell--info-2" rowspan="2">${unitLabel}</th>
                  <th class="size-guide__cup-bar" colspan="${cupCount}"><span>${xAxisLabel}</span></th>
                </tr>
                <tr class="size-guide__letter-row">${cupLetters}</tr>
              </thead>
              <tbody>${bodyRows}</tbody>
            </table>
          </div>
          ${footnoteHtml}
        </div>
      `;
    };

    // Simple (panty) column layout comes from the data, not from fixed positions: the size
    // column is the one whose body cells aren't a numeric range ("62 - 67"); every other column
    // is a measurement, kept in the chart's own order. Used by both the table and the calculator,
    // so a client can re-order the metaobject columns (or add one) without breaking either.
    const resolveSimpleColumns = ({ tableValues }) => {
      const sample = tableValues[1] ?? tableValues[0] ?? [];
      let sizeCol = sample.findIndex((value) => !isRangeValue(value));
      if (sizeCol === -1) sizeCol = sample.length - 1; // all ranges → fall back to the last column
      const valueCols = sample.map((_, index) => index).filter((index) => index !== sizeCol);
      return { sizeCol, valueCols };
    };

    // Simple (panty) new-mode table — alternating row shading, footnote. Columns render in the
    // metaobject's own order (the client decides whether Size or Waist comes first); only the
    // size column is identified by content, so it keeps its styling and drives data-row-size,
    // which a calculator match uses to highlight the whole row (State B).
    const createSimpleTable = ({ selectedChart, unit }) => {
      const { tableValues } = selectedChart ?? {};
      if (!tableValues || tableValues.length === 0) return '';

      const [headings, ...rows] = tableValues;
      const { sizeCol } = resolveSimpleColumns({ tableValues });
      const sizeClass = ' class="size-guide__simple-size"';

      const head = `<tr>
        ${headings.map((heading, col) => `<th${col === sizeCol ? sizeClass : ''}>${heading ?? ''}</th>`).join('')}
      </tr>`;

      const body = rows
        .map((row) => {
          const cells = row
            .map((value, col) => {
              if (col === sizeCol) return `<td${sizeClass}>${value}</td>`;
              return `<td>${unit === 'inch' ? convertCmToInch({ value }) : value}</td>`;
            })
            .join('');
          return `<tr data-row-size="${row[sizeCol]}">${cells}</tr>`;
        })
        .join('');

      // Unit can come from the unit toggle OR from a separate size-system chart
      // (e.g. a chart literally titled "Inches"), so honour both.
      // Footnote comes ONLY from the chart's own fields (unit line + optional legend);
      // nothing shows until the client fills them — same rule as the complex chart.
      const unitLine = selectedChart?.footnote || '';
      const legend = selectedChart?.footnoteLegend || '';
      const footnoteHtml =
        unitLine || legend
          ? `<p class="size-guide__table-footnote">${unitLine ? `<span class="size-guide__table-footnote-unit">${unitLine}</span>` : ''}${legend ? `<span class="size-guide__table-footnote-legend">${legend}</span>` : ''}</p>`
          : '';

      return `
        <div class="size-guide__table-outer size-guide__table-outer--simple">
          <div class="size-guide__table-inner">
            <table>
              <thead>${head}</thead>
              <tbody>${body}</tbody>
            </table>
          </div>
          ${footnoteHtml}
        </div>
      `;
    };

    const createTableContainer = ({ selectedChart, title, unit, chartDescription }) => {
      const createAxisLabel = ({ axis, label }) => {
        if (!axis) return;

        const icon =
          "<svg xmlns='http://www.w3.org/2000/svg' width='27' height='9' viewBox='0 0 27 9' fill='none'><path d='M26.3536 4.85355C26.5488 4.65829 26.5488 4.34171 26.3536 4.14645L23.1716 0.964466C22.9763 0.769204 22.6597 0.769204 22.4645 0.964466C22.2692 1.15973 22.2692 1.47631 22.4645 1.67157L25.2929 4.5L22.4645 7.32843C22.2692 7.52369 22.2692 7.84027 22.4645 8.03553C22.6597 8.2308 22.9763 8.2308 23.1716 8.03553L26.3536 4.85355ZM0 5H26V4H0V5Z' fill='#62554A'></path></svg>";

        return label
          ? `<div class="size-guide__table-label size-guide__table-label--${axis}-axis">${label} ${icon}</div>`
          : '';
      };

      const createTable = ({ tableValues, unit }) => {
        if (!tableValues || tableValues.length === 0) return;
        const [headings, ...rows] = tableValues;

        const createRow = ({ tag, cells, unit }) => `
          <tr>
            ${cells
              .map((cell) => {
                // Hover/tap reveal is complex-only (see createComplexTable). Here (simple/default)
                // just show the visible text and strip any "::hidden" — no data-size, no reveal.
                const [visible] = String(cell).split('::');
                const display = unit === 'inch' ? convertCmToInch({ value: visible }) : visible;
                return `<${tag}>${display}</${tag}>`;
              })
              .join('')}
          </tr>
        `;

        return `
          <table>
            <thead>${createRow({ tag: 'th', cells: headings, unit })}</thead>
            <tbody>${rows.map((row) => createRow({ tag: 'td', cells: row, unit })).join('')}</tbody>
          </table>
        `;
      };

      // Complex (bralette): custom chrome (Band Size bar, Cup Size bar, intro corner)
      if (selectedChart?.mode === 'complex') {
        return createComplexTable({ selectedChart, unit });
      }

      // Simple (panty) new mode: SIZE | WAIST | HIP layout from the redesign
      if (selectedChart?.mode === 'simple') {
        return createSimpleTable({ selectedChart, unit });
      }

      const { tableValues, xAxisLabel, yAxisLabel } = selectedChart ?? {};
      const xAxisHtml = createAxisLabel({ axis: 'x', label: xAxisLabel });
      const yAxisHtml = createAxisLabel({ axis: 'y', label: yAxisLabel });
      const tableHtml = createTable({ tableValues, unit });
      const sizeGuideTitle = selectedChart?.sizeGuideTitle ?? title;

      return `
        ${sizeGuideTitle ? `<h3 class="size-guide__table-title h3">${sizeGuideTitle}</h3>` : ''}
        ${chartDescription ? `<p class="size-guide__table-description">${chartDescription}</p>` : ''}
        <div class="size-guide__table-outer">
          ${xAxisHtml}

          <div class="size-guide__table-inner">
            ${yAxisHtml}
            ${tableHtml}
          </div>
        </div>
      `;
    };

    const createChartSwitch = ({ charts, sectionIndex }) => {
      const inputsHtml = charts
        .map(
          (chart, index) =>
            `<label class="text-label">
              <input type="radio"
              class="sr-only"
              name="size-guide-${sectionIndex}-sizing-system"
              value="${chart.title}"
              ${index === 0 ? 'checked' : ''}
            >
              ${chart.title}
            </label>`,
        )
        .join('');

      return `
        <div class="size-guide__switch size-guide__switch--chart">
          ${inputsHtml}
        </div>
      `;
    };

    const createUnitSwitch = ({ sectionIndex, selectedChart }) => {
      const units = [
        { name: locale.centimeters, value: 'cm' },
        { name: locale.inches, value: 'inch' },
      ];

      const isConversionToInchesAllowed = selectedChart.isConversionToInchesAllowed;

      const inputsHtml = units
        .map(
          ({ name, value }, index) =>
            `<label>
              <input
                type="radio"
                name="size-guide-${sectionIndex}-unit"
                value="${value}"
                ${index === 0 ? 'checked' : ''}
              >
              ${name}
            </label>`,
        )
        .join('');

      return `
        <div class="size-guide__switch size-guide__switch--unit ${isConversionToInchesAllowed ? '' : 'hidden'}">
          ${inputsHtml}
        </div>
      `;
    };

    const createInterface = ({ charts, title, selectedChart, sectionIndex, unit }) => {
      const chartSwitchHtml = createChartSwitch({ charts, sectionIndex });
      const unitSwitchHtml = createUnitSwitch({ sectionIndex, selectedChart });
        const tableContainerHtml = createTableContainer({ 
          selectedChart, 
          title, 
          unit,
          chartDescription: selectedChart?.additionalDynamicText
        });

      return `
        <div class="size-guide__toolbar">
          ${chartSwitchHtml}
          ${unitSwitchHtml}
        </div>

        ${tableContainerHtml}
      `;
    };

    const renderContent = ({ container, additionalChartContainers, charts, additionalGuides, titles, selectedChart, unit, sectionIndex }) => {
      if (!container) return;

      const isEmptyContainer = container.querySelector(selectors.chartPlaceholder);
      const getContent = isEmptyContainer ? createInterface : createTableContainer;

      container.innerHTML = getContent({
        charts,
        selectedChart,
        unit,
        sectionIndex,
        chartDescription: selectedChart?.additionalDynamicText,
      });

      if (additionalChartContainers.length > 0) {
        additionalChartContainers.forEach((container, index) => {
          const additionalCharts = getAdditionalCharts(additionalGuides[index]);
          const selectedChart = additionalCharts[0];
          const guideTitle = titles ? titles.split(',')[index] : '';
          const hasMultipleCharts = additionalCharts && additionalCharts.length > 1;
          const getContent = hasMultipleCharts ? createInterface : createTableContainer;

          container.innerHTML = getContent({
            charts: additionalCharts,
            title: guideTitle,
            selectedChart,
            unit,
            sectionIndex: `${sectionIndex}-additional-${index}`,
          });
        });
      }
    };

    const updateAdditionalChartTable = ({ container, charts, selectedChart, unit }) => {
      const tableWrapper = container.querySelector(selectors.tableWrapper);
      const existingDescriptions = container.querySelectorAll('.size-guide__table-description');
      existingDescriptions.forEach(desc => desc.remove());
      
      const tableContainerHtml = createTableContainer({
        selectedChart,
        unit,
        chartDescription: selectedChart?.additionalDynamicText
      });
      tableWrapper.outerHTML = tableContainerHtml;
    };

    const initAdditionalChartHandlers = ({ container, charts, sectionIndex }) => {
      container.addEventListener('change', (event) => {
        const unitSwitch = container.querySelector(selectors.unitSwitch);
        const chartSwitch = container.querySelector(selectors.chartSwitch);
        
        if (event.target.closest(selectors.chartSwitch)) {
          const selectedUnit = unitSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === event.target.value);

          unitSwitch?.classList?.toggle('hidden', !selectedChart.isConversionToInchesAllowed);
          
          updateAdditionalChartTable({
            container,
            charts,
            selectedChart,
            unit: selectedChart.isConversionToInchesAllowed ? selectedUnit : 'cm'
          });
        }

        if (event.target.closest(selectors.unitSwitch)) {
          const chartSwitchValue = chartSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === chartSwitchValue);

          updateAdditionalChartTable({
            container,
            charts,
            selectedChart,
            unit: event.target.value
          });
        }
      });
    };

    const initChartHandlers = ({ section, charts, additionalGuides, titles, sectionIndex }) => {
      const filteredCharts = charts.filter((chart) => !chart.hideSizeChart);
      const chartContainer = section.querySelector(selectors.chartContainer);
      const additionalChartContainers = section.querySelectorAll(selectors.additionalChartContainer);
      if (!chartContainer) return;
      const selectedChart = filteredCharts[0];

      renderContent({
        container: chartContainer,
        additionalChartContainers,
        charts: filteredCharts,
        additionalGuides: additionalGuides,
        titles: titles,
        selectedChart: selectedChart,
        sectionIndex,
      });

      // IMPORTANT: Search inside chartContainer, not section, to avoid getting the first tableWrapper from additional charts
      const tableWrapper = chartContainer.querySelector(selectors.tableWrapper);
      const unitSwitch = chartContainer.querySelector(selectors.unitSwitch);
      const chartSwitch = chartContainer.querySelector(selectors.chartSwitch);
      
      chartContainer.addEventListener('change', (event) => {
        if (event.target.closest(selectors.chartSwitch)) {
          const selectedUnit = unitSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === event.target.value);
          
          unitSwitch?.classList?.toggle('hidden', !selectedChart.isConversionToInchesAllowed);

          renderContent({
            container: tableWrapper,
            additionalChartContainers,
            charts: filteredCharts,
            titles: titles,
            additionalGuides: additionalGuides,
            selectedChart: selectedChart,
            unit: selectedChart.isConversionToInchesAllowed ? selectedUnit : 'cm',
            sectionIndex,
          });
        }

        if (event.target.closest(selectors.unitSwitch)) {
          const chartSwitchValue = chartSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === chartSwitchValue);

          renderContent({
            container: tableWrapper,
            additionalChartContainers,
            charts: filteredCharts,
            titles: titles,
            additionalGuides: additionalGuides,
            selectedChart,
            unit: event.target.value,
            sectionIndex,
          });
        }

        // Table was rebuilt → re-apply the calculator match highlight (was lost on re-render).
        if (section.__sgHighlight) {
          requestAnimationFrame(() => section.__sgHighlight(section.__sgMatched));
        }
      });

      if (additionalChartContainers.length > 0) {
        additionalChartContainers.forEach((container, index) => {
          const additionalCharts = getAdditionalCharts(additionalGuides[index]);
          initAdditionalChartHandlers({
            container,
            charts: additionalCharts,
            sectionIndex: `${sectionIndex}-additional-${index}`,
          });
        });
      }
    };

    const initPopupHandlers = ({ section }) => {
      const popupTabContents = section.querySelectorAll(selectors.popupTabContent);

      section.addEventListener('change', (event) => {
        if (event.target.closest(selectors.popupTabs)) {
          popupTabContents.forEach((item) =>
            item.classList.toggle('active', item.dataset.tabId === event.target.value),
          );
        }
      });
    };

    // New mode (simple/complex) popup: two steps — calculator (calc) and full chart (chart).
    // The "View full size chart" / "Back to size calculator" links flip the active step.
    // Exposes section.__sgGoto so the calculator can jump to the chart after a match.
    const initStepHandlers = ({ section }) => {
      const body = section.querySelector('.size-guide__popup-body--stepped');
      if (!body) return;

      const steps = body.querySelectorAll('.size-guide__step');
      const popupContent = section.querySelector('.popup__content');
      const summaryEl = section.querySelector('.size-guide__chart-summary');

      // Header title swaps per step (design): "SIZE GUIDE" on the calculator, "SIZE CHART" on the chart.
      const titleEl = section.querySelector('.popup__title');
      const calcTitle = titleEl ? titleEl.textContent.trim() : '';

      // Move the reserved summary INTO the chart container so it's a sibling of the toolbar:
      // lets them share one centered row on desktop and the same width as the chart on mobile.
      // Safe because chart/unit switches only re-render the table, not the toolbar or summary.
      const chartContainerEl = section.querySelector(selectors.chartContainer);
      if (summaryEl && chartContainerEl && !chartContainerEl.contains(summaryEl)) {
        chartContainerEl.insertBefore(summaryEl, chartContainerEl.firstChild);
      }
      // Same for the reveal hint — mobile order is summary → toggle → hint → table (design);
      // desktop re-places it via the grid areas, so DOM order only matters on mobile.
      const hintEl = section.querySelector('.size-guide__chart-hint');
      if (hintEl && chartContainerEl && !chartContainerEl.contains(hintEl)) {
        const toolbarEl = chartContainerEl.querySelector('.size-guide__toolbar');
        (toolbarEl || summaryEl)?.after(hintEl);
      }

      // Highlight the match on the chart step:
      // complex → the cup cell carrying the hidden size token; simple → the whole row.
      const highlightMatched = (sizes) => {
        const container = section.querySelector(selectors.chartContainer);
        if (!container) return;
        // is-matched = the calculator result (persistent). is-revealed also triggers the reveal.
        container
          .querySelectorAll('.is-revealed, .is-matched')
          .forEach((el) => el.classList.remove('is-revealed', 'is-matched'));
        if (!sizes || sizes.length === 0) return;

        const mark = (el) => el.classList.add('is-revealed', 'is-matched');

        // Complex + a calculator match → highlight ONLY the exact band × bust cell (design),
        // not every cell that happens to carry the same size.
        const matchCell = section.__sgMatchCell;
        if (matchCell) {
          const tbody = container.querySelector('.size-guide__table-outer--complex table tbody');
          const td = tbody?.querySelectorAll('tr')[matchCell.row]?.querySelectorAll('td')[matchCell.col];
          if (td) {
            mark(td);
            return;
          }
        }

        container.querySelectorAll('td[data-size]').forEach((cell) => {
          const tokens = String(cell.dataset.size).split('/');
          if (tokens.some((token) => sizes.includes(token))) mark(cell);
        });
        container.querySelectorAll('tr[data-row-size]').forEach((row) => {
          if (sizes.includes(row.dataset.rowSize)) mark(row);
        });
      };
      // Exposed so a chart/unit switch (which re-renders the table) can re-apply the match.
      section.__sgHighlight = highlightMatched;

      // Reserved summary on the chart step: found size carried over, else the default message.
      // The note differs by source (Customize): 'manual' = clicked a cell, else = from the calculator.
      const renderSummary = (sizes, source) => {
        if (!summaryEl) return;
        if (sizes && sizes.length) {
          const note = source === 'manual' ? settings.resultNoteManual : settings.resultNoteCalc;
          summaryEl.innerHTML = `
            <p class="size-guide__chart-summary-label">${locale.yourSizeIs}</p>
            <p class="size-guide__chart-summary-size">${sizes.join(` ${locale.or} `)}</p>
            ${note ? `<p class="size-guide__chart-summary-note">${note}</p>` : ''}
          `;
          summaryEl.classList.add('is-result');
        } else {
          summaryEl.innerHTML = summaryEl.dataset.default || '';
          summaryEl.classList.remove('is-result');
        }
      };
      renderSummary(null); // seed the default so the area never looks empty
      // Exposed so a manual cell reveal (complex) can echo the picked size in the summary.
      section.__sgRenderSummary = renderSummary;

      // Complex only: a size-system / unit switch re-renders the chart table and drops the
      // highlight. initChartHandlers' own re-apply doesn't fire reliably for the complex
      // chart-switch, so re-apply here at the section level (guaranteed to catch the bubbled
      // change) using the in-scope highlight/summary. Idempotent — highlightMatched clears first.
      if (section.dataset.mode === 'complex') {
        section.addEventListener('change', (event) => {
          if (event.target.closest(selectors.chartSwitch) || event.target.closest(selectors.unitSwitch)) {
            requestAnimationFrame(() => {
              highlightMatched(section.__sgMatched);
              renderSummary(section.__sgMatched);
            });
          }
        });
      }

      const goto = (name) => {
        steps.forEach((step) => step.classList.toggle('is-active', step.dataset.sgStep === name));
        if (popupContent) popupContent.scrollTop = 0;
        if (titleEl) titleEl.textContent = name === 'chart' ? (locale.chartTitle || calcTitle) : calcTitle;
        if (name === 'chart') {
          // chart was display:none until now → re-apply any match once it's visible
          requestAnimationFrame(() => {
            highlightMatched(section.__sgMatched);
            renderSummary(section.__sgMatched);
          });
        }
      };

      section.__sgGoto = goto;

      body.addEventListener('click', (event) => {
        const link = event.target.closest('[data-sg-goto]');
        if (!link) return;
        event.preventDefault();
        goto(link.dataset.sgGoto);
      });
    };

    const setFormFieldsPlaceholders = ({ formFields, selectedChart, firstField, secondField, useCustomFields }) => {
      // simple/complex: chart placeholder fields first, else fall back to the table's
      // first two header cells (like live) — e.g. panty header "Waist"/"Hip".
      if (selectedChart?.mode === 'simple' || selectedChart?.mode === 'complex') {
        const [firstHeading, secondHeading] = selectedChart?.tableValues?.[0] || [];
        const placeholders = [
          selectedChart?.sizeCalculatorFirstInputPlaceholder || firstHeading || '',
          selectedChart?.sizeCalculatorSecondInputPlaceholder || secondHeading || '',
        ];
        formFields.forEach((field, index) => {
          field.placeholder = placeholders[index] || '';
        });
        return;
      }

      let [, firstTableHeading, secondTableHeading] = selectedChart?.tableValues?.[0] || [];
      if (useCustomFields && firstField && secondField) {
        if (selectedChart?.pantySizeGuide == 'false') {
          firstTableHeading = selectedChart?.sizeCalculatorFirstInputPlaceholder ? secondField : firstField;
          secondTableHeading = selectedChart?.sizeCalculatorSecondInputPlaceholder ? firstField : secondField;
        }
      } else {
        if (selectedChart?.isPrimaryChart) {
          firstTableHeading = firstField || 'Cup size';
          secondTableHeading = secondField || 'Band size';
        }
      }

      if (selectedChart?.pantySizeGuide === 'true') {
        firstTableHeading = selectedChart?.sizeCalculatorFirstInputPlaceholder || firstTableHeading;
        secondTableHeading = selectedChart?.sizeCalculatorSecondInputPlaceholder || secondTableHeading;
      }

      const placeholders = selectedChart ? [firstTableHeading, secondTableHeading] : [firstField, secondField];
      
      formFields.forEach((field, index) => {
        field.placeholder = placeholders[index] || '';
      });
    };

    const findSizeInTableHeadings = ({ tableValues, firstMeasurement, secondMeasurement, unit }) => { 
      if (!tableValues || tableValues.length === 0) return

      const parseValue = ({ value, unit }) => {
        return unit === 'inch' ? convertCmToInch({ value }) : Number(value);
      };
      const matches = firstMeasurement.match(/(\d+)([a-zA-Z]+)/);
      const hasSingleMeasurement = firstMeasurement && !secondMeasurement;
      const firstValue = hasSingleMeasurement ? matches[2] : firstMeasurement;
      const secondValue = hasSingleMeasurement ? Number(matches[1]) : Number(secondMeasurement);
      const [headingRow] = tableValues;
      const columnIndex = headingRow.indexOf(firstValue);

      if (columnIndex === -1) return []
      
      const rowIndexes = tableValues.reduce((accumulator, row, index) => {
        const isHeadingRow = index === 0;
        if (isHeadingRow) return accumulator;

        const currentRowValue = parseValue({ value: row[0], unit });
        const nextRowValue = parseValue({ value: tableValues[index + 1]?.[0], unit });

        if (currentRowValue === secondValue) {
          accumulator.push(index);
        } else if (currentRowValue < secondValue && nextRowValue && secondValue < nextRowValue) {
          accumulator.push(index, index + 1);
        }

        return accumulator;
      }, []);

      const sizeMatches = rowIndexes.map((rowIndex) => tableValues[rowIndex][columnIndex]).filter(Boolean);
      return sizeMatches;
    };

    const findSizeInTableBody = ({ tableValues, firstMeasurement, secondMeasurement, unit }) => {
      if (!tableValues || tableValues.length === 0) return

      const parseRange = ({ range, unit }) => {
        const [min, max] = splitRange(range).map((value) =>
          unit === 'inch' ? convertCmToInch({ value }) : Number(value),
        );

        return { min, max };
      };

      const sortBySize = ({ sizes }) => {
        const sizesOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
        return sizes.sort((a, b) => sizesOrder.indexOf(a) - sizesOrder.indexOf(b));
      };

      const findSizeMatches = ({ tableValues }) => {
        const sizeMatches = tableValues.reduce(
          (accumulator, [size, firstRange, secondRange], index) => {
            if (index === 0) return accumulator;

            const { min: firstRangeMin, max: firstRangeMax } = parseRange({ range: firstRange, unit });
            const { min: secondRangeMin, max: secondRangeMax } = parseRange({ range: secondRange, unit });

            if (firstMeasurement >= firstRangeMin && firstMeasurement <= firstRangeMax) {
              accumulator.firstMatches.push({ size, index });
            }

            if (secondMeasurement >= secondRangeMin && secondMeasurement <= secondRangeMax) {
              accumulator.secondMatches.push({ size, index });
            }

            return accumulator;
          },
          { firstMatches: [], secondMatches: [] },
        );

        return sizeMatches;
      };

      const getConsecutiveMatches = ({ sizeMatches }) => {
        const { firstMatches, secondMatches } = sizeMatches;
        const sizes = [];

        firstMatches.forEach((firstMatch) => {
          const match = secondMatches.find((secondMatch) => {
            const isSameSize = firstMatch.size === secondMatch.size;
            const isConsecutiveOrder = Math.abs(firstMatch.index - secondMatch.index) <= 1;

            return isSameSize && isConsecutiveOrder;
          });

          if (match) sizes.push(firstMatch.size)
        });
        
        const sortedSizes = sortBySize({ sizes });
        
        return sortedSizes;
      };

      const sizeMatches = findSizeMatches({ tableValues });
      const consecutiveMatches = getConsecutiveMatches({ sizeMatches });

      return consecutiveMatches;
    };

    // complex (bralette): underbust -> row (functional col), bust -> cup cell -> hidden size token(s)
    // simple (panty): the two inputs are matched against the chart's measurement columns, in the
    // chart's own order (see resolveSimpleColumns) — no fixed column positions.
    const findSizeSimple = ({ tableValues, firstMeasurement, secondMeasurement }) => {
      if (!tableValues || tableValues.length === 0) return [];

      const waist = Number(firstMeasurement);
      const hip = Number(secondMeasurement);
      if (Number.isNaN(waist) || Number.isNaN(hip)) return [];

      const inRange = (range, value) => {
        const [min, max] = splitRange(range).map(Number);
        return !Number.isNaN(min) && !Number.isNaN(max) && value >= min && value <= max;
      };

      // Column roles read from the data; input 1 goes to the first measurement column, input 2
      // to the second. Skip the heading row.
      const { sizeCol, valueCols } = resolveSimpleColumns({ tableValues });
      const [firstCol, secondCol] = valueCols;
      if (firstCol === undefined || secondCol === undefined) return [];

      const rows = tableValues.slice(1);
      const sizeOf = (row) => row[sizeCol];
      const waistRow = rows.find((row) => inRange(row[firstCol], waist));
      const hipRow = rows.find((row) => inRange(row[secondCol], hip));

      // Each measurement must land in a band. If they resolve to different sizes, recommend
      // the LARGER one (design: fit to the bigger measurement) instead of failing to match.
      if (!waistRow || !hipRow) return [];
      const wSize = sizeOf(waistRow);
      const hSize = sizeOf(hipRow);
      if (wSize === hSize) return [wSize];

      // Sizes differ. Default: no match (waist and hip must agree — live behaviour).
      // Opt-in via theme setting: recommend the larger of the two with a split-fit note.
      if (!settings.splitFit) return [];

      const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
      const wi = order.indexOf(wSize);
      const hi = order.indexOf(hSize);
      if (wi === -1 || hi === -1) return [wSize]; // unknown label → fall back to the waist size
      // Split fit: sizes differ → recommend the larger and flag it so the result can explain why.
      const out = [wi >= hi ? wSize : hSize];
      out.isSplitFit = true;
      return out;
    };

    const findSizeComplex = ({ tableValues, firstMeasurement, secondMeasurement }) => {
      if (!tableValues || tableValues.length === 0) return [];

      const underbust = Number(firstMeasurement);
      const bust = Number(secondMeasurement);
      if (Number.isNaN(underbust) || Number.isNaN(bust)) return [];

      const inRange = ({ range, value }) => {
        const [min, max] = splitRange(range).map(Number);
        return !Number.isNaN(min) && !Number.isNaN(max) && value >= min && value <= max;
      };

      // Two informational left columns (underbust range + band number), then cup cells
      const CUP_START_COL = 2;
      const dataRows = tableValues.slice(1);

      // underbust = the left column holding a range; the band column is a plain number.
      // Auto-detected so column order (underbust-first or band-first) doesn't matter, and
      // matched by shape rather than by " - ", since the data may be typed without spaces.
      const sample = dataRows[0] ?? [];
      let underbustCol = 0;
      for (let i = 0; i < CUP_START_COL; i += 1) {
        if (isRangeValue(sample[i])) {
          underbustCol = i;
          break;
        }
      }

      // First matching underband row wins
      const rowIndex = dataRows.findIndex((cells) => inRange({ range: cells[underbustCol], value: underbust }));
      if (rowIndex === -1) return [];
      const row = dataRows[rowIndex];

      for (let col = CUP_START_COL; col < row.length; col += 1) {
        const [visible, hidden] = String(row[col] ?? '').split('::');
        if (!hidden) continue;
        if (inRange({ range: visible, value: bust })) {
          // "/" splits dual sizes -> renderSearchResults joins with locale.or; "*" kept (client controls via content)
          const result = hidden.split('/').filter(Boolean);
          // Exact band × bust cell → the chart highlights just this one (design), not every cell of that size.
          result.cell = { row: rowIndex, col };
          return result;
        }
      }
      return [];
    };

    const renderSearchResults = ({ container, matchedSizes, matchedVariants, isSplitFit }) => {
      if (!container) return;

      container.classList.add('is-result'); // a search ran → leave the default-message state

      const noSizeHtml = `
        <div class="size-guide__popup-calculator-result-heading">${locale.noSize}</div>
        <p class="size-guide__popup-calculator-result-heading">${locale.contactUs}</p>
      `;

      if (!matchedSizes || matchedSizes.length === 0) {
        container.innerHTML = noSizeHtml;
        return;
      }

      const { sizingSuggestion } = container.dataset;
      // Split-fit note (waist/hip in different bands) takes priority over the metafield suggestion.
      const noteText = isSplitFit && locale.splitFitNote
        ? locale.splitFitNote
        : matchedSizes.length > 1 && sizingSuggestion
          ? sizingSuggestion
          : '';
      const sizingSuggestionHtml = noteText
        ? `<p class="size-guide__popup-calculator-result-info">${noteText}</p>`
        : '';

      const firstVariant = matchedVariants && matchedVariants.length === 1 ? matchedVariants[0] : null;
      
      const addToCartHtml =
        firstVariant && firstVariant.available
          ? `<button type="button" class="size-guide__popup-calculator-result-button btn btn--minimalist btn--small" data-variant-id="${firstVariant.id}">${locale.addToCart}</button>`
          : '';

      const foundSizeHtml = `
        <p class="size-guide__popup-calculator-result-heading">${locale.yourSizeIs}</p>
        <p class="size-guide__popup-calculator-result-size">${matchedSizes.join(` ${locale.or} `)}</p>
        ${sizingSuggestionHtml}
        ${addToCartHtml}
      `;

      container.innerHTML = matchedSizes.length ? foundSizeHtml : noSizeHtml;
    };

    // New-mode (simple/complex) reserved result area: seed the default message so the
    // card keeps its footprint before a search (no layout jump on first result).
    const renderDefaultResult = (container) => {
      if (!container) return;
      const message = container.dataset.default;
      if (!message) return;
      container.innerHTML = `<p class="size-guide__popup-calculator-result-default">${message}</p>`;
      container.classList.remove('is-result');
    };

    const initCalculatorHandlers = ({ section, charts }) => {
      const chartContainer = section.querySelector(selectors.chartContainer);
      const calculatorForm = section.querySelector(selectors.calculatorForm);
      if (!calculatorForm) return; // guard before the querySelector below dereferences it
      const calculatorResult = section.querySelector(selectors.calculatorResult);
      const unitSwitch = calculatorForm.querySelector(selectors.unitSwitch);

      const hasMissingElements = [chartContainer, calculatorForm, calculatorResult, unitSwitch].some((element) => !element);
      if (hasMissingElements) {
        return;
      }

      const variantsJson = document.querySelector('[data-combined-variant-json]')
        ? document.querySelector('[data-combined-variant-json]')
        : document.querySelector('[data-variant-json]');
      const variants = JSON.parse(variantsJson?.innerHTML) ?? [];
      const chartSwitchHtml = createChartSwitch({ charts, sectionIndex: 'calculator' });
      calculatorForm.insertAdjacentHTML('afterbegin', chartSwitchHtml);

      const formFields = calculatorForm.querySelectorAll(selectors.calculatorFormField);
      const firstFieldValue = charts[0].sizeCalculatorFirstInputPlaceholder || locale.enterCupSize;
      const secondFieldValue = charts[0].sizeCalculatorSecondInputPlaceholder || locale.enterBandSize;

      setFormFieldsPlaceholders({ formFields, selectedChart: charts[0], firstField: firstFieldValue, secondField: secondFieldValue, useCustomFields: true });

      // New mode reserves the result area — show the default message until a search runs.
      const isNewMode = section.dataset.mode === 'simple' || section.dataset.mode === 'complex';
      if (isNewMode) renderDefaultResult(calculatorResult);

      formFields.forEach((field) => {
        field.addEventListener('input', (event) => {
          let inputValue = event.target.value;

          inputValue = inputValue.replace(/,/g, '.');
          inputValue = inputValue.replace(/[^a-zA-Z0-9,.]/g, '');
          inputValue = inputValue.toUpperCase();

          event.target.value = inputValue;
        });
      });

      calculatorForm.addEventListener('change', (event) => {
        if (!event.target.closest(selectors.chartSwitch)) return;
        
        const selectedChart = charts.find((chart) => chart.title === event.target.value);
        
        setFormFieldsPlaceholders({
          formFields,
          selectedChart,
          firstField: selectedChart.sizeCalculatorFirstInputPlaceholder || locale.enterCupSize,
          secondField: selectedChart.sizeCalculatorSecondInputPlaceholder || locale.enterBandSize,
          useCustomFields: true
        });

        unitSwitch.classList.toggle('hidden', !selectedChart.isConversionToInchesAllowed);
      });

      calculatorForm.addEventListener('submit', (event) => {
        event.preventDefault();
        
        const formData = new FormData(calculatorForm);
        
        const {
          first,
          second,
          'size-guide-calculator-sizing-system': sizingSystem,
          'size-guide-calculator-unit': unit,
        } = Object.fromEntries(formData);
        
        // New mode: no native `required` — show a custom empty-fields error in the reserved area.
        if (isNewMode && (!String(first || '').trim() || !String(second || '').trim())) {
          section.__sgMatched = [];
          section.__sgMatchCell = null;
          calculatorResult.classList.add('is-result');
          // use the message (default) style, not the heading — the heading is uppercase.
          calculatorResult.innerHTML = `<p class="size-guide__popup-calculator-result-default">${settings.errorEmpty || ''}</p>`;
          return;
        }

        const selectedChart = charts.find((chart) => chart.title === sizingSystem);
        const hasAxisLabels = !!(selectedChart.xAxisLabel || selectedChart.yAxisLabel);
        const findSize =
          selectedChart.mode === 'complex'
            ? findSizeComplex
            : selectedChart.mode === 'simple'
              ? findSizeSimple
              : hasAxisLabels
                ? findSizeInTableHeadings
                : findSizeInTableBody;
        const searchParams = {
          tableValues: selectedChart.tableValues,
          firstMeasurement: first,
          secondMeasurement: second,
          ...(selectedChart.isConversionToInchesAllowed && { unit }),
        };
        const matchedSizes = findSize(searchParams);
        section.__sgMatched = matchedSizes; // remembered so the chart step can highlight the matched cell
        section.__sgMatchCell = matchedSizes?.cell || null; // complex: exact band × bust cell to highlight
        const matchedVariants = matchedSizes?.map((size) =>
          variants.find((variant) => variant.options.some((option) => option === size)),
        );

        renderSearchResults({
          container: calculatorResult,
          matchedSizes,
          matchedVariants,
          isSplitFit: !!matchedSizes?.isSplitFit,
        });
      });

      calculatorResult.addEventListener('click', (event) => {
        if (!event.target.closest(selectors.calculatorResultButton)) return;

        const { variantId } = event.target.dataset;
        
        if (variantId) window.addToCart(variantId, 1);

        event.target.closest(selectors.dialog).close();
      });
    };

    const getCharts = ({ section }) => {
      const isValidJson = ({ string }) => {
        try {
          JSON.parse(string);
        } catch (error) {
          return false;
        }
        return true;
      };

      const mapTableRows = ({ tableRows }) => {
        const mapRow = (row) => row.split('|').map(mapCell);
        const mapCell = (cell) => (cell === '-' ? '' : cell);

        return (tableRows ?? []).map(mapRow);
      };

      const jsonString = section.querySelector(selectors.chartsJson)?.textContent;
      if (!isValidJson({ string: jsonString })) return;

      const mode = section.dataset.mode;
      const useInteractiveRows = mode === 'simple' || mode === 'complex';
      const introHtml = section.querySelector(selectors.intro)?.innerHTML ?? '';

      const charts = JSON.parse(jsonString);

      return charts.map((chart, index) => {

        const {
          title,
          x_axis_label: xAxisLabel,
          y_axis_label: yAxisLabel,
          table_rows: tableRows,
          simple_complex_table_rows: interactiveRows,
          is_conversion_to_inches_allowed: isConversionToInchesAllowed,
          hide_size_chart: hideSizeChart,
          additional_dynamic_text: additionalDynamicText,
        } = chart;

        const rows = useInteractiveRows && interactiveRows?.length ? interactiveRows : tableRows;

        return {
          sizeGuideTitle: section.dataset.sizeGuideTitle,
          title,
          mode,
          introHtml,
          ...(xAxisLabel && { xAxisLabel }),
          ...(yAxisLabel && { yAxisLabel }),
          ...(additionalDynamicText && { additionalDynamicText }),
          tableValues: mapTableRows({ tableRows: rows }),
          isConversionToInchesAllowed: !!isConversionToInchesAllowed,
          isPrimaryChart: !index,
          pantySizeGuide: section.dataset.pantySizeGuide,
          hideSizeChart: !!hideSizeChart,
          sizeCalculatorFirstInputPlaceholder: chart.placeholder_for_the_first_input_of_the_size_calculator,
          sizeCalculatorSecondInputPlaceholder: chart.placeholder_for_the_second_input_of_the_size_calculator,
          // Per-chart fields (metaobject) — they live on the chart, so they switch automatically
          // with the visible chart toggle (EU/JP↔UK/US, or cm↔inches panty):
          //   simple_complex_table_unit_label → 2nd column header, e.g. "CM" / "INCH"
          //   footnote        → footnote unit line, "All measurements in cm." / "…inches."
          //   footnote_legend → footnote cup key, "A–B cups = regular…"
          //     (legend differs per system: EU cups A–F vs UK A,B,C,D,DD,DDD(E)).
          unitLabel: chart.simple_complex_table_unit_label,
          footnote: chart.footnote,
          footnoteLegend: chart.footnote_legend,
        };
      });
    };

    const getAdditionalGuides = ({ section }) => {
      const isValidJson = ({ string }) => {
        try {
          JSON.parse(string);
        } catch (error) {
          return false;
        }
        return true;
      };

      const jsonString = section.querySelector(selectors.additionalChartsJson)?.textContent;
      const titles = section.querySelector(selectors.additionalChartsJson)?.dataset.additionalSizeTitles;

      if (!isValidJson({ string: jsonString })) return [[], ''];

      const guides = JSON.parse(jsonString);
      return [guides, titles || '']
    };
    
    const getAdditionalCharts = (charts) => {
      const mapTableRows = ({ tableRows }) => {
        const mapRow = (row) => row.split('|').map(mapCell);
        const mapCell = (cell) => (cell === '-' ? '' : cell);

        return (tableRows ?? []).map(mapRow);
      };

      return charts.map((chart, index) => {
        const {
          title,
          x_axis_label: xAxisLabel,
          y_axis_label: yAxisLabel,
          table_rows: tableRows,
          is_conversion_to_inches_allowed: isConversionToInchesAllowed,
          hide_size_chart: hideSizeChart,
          additional_dynamic_text: additionalDynamicText,
        } = chart;

        return {
          title,
          ...(xAxisLabel && { xAxisLabel }),
          ...(yAxisLabel && { yAxisLabel }),
          ...(additionalDynamicText && { additionalDynamicText }),
          tableValues: mapTableRows({ tableRows }),
          isConversionToInchesAllowed: !!isConversionToInchesAllowed,
          isPrimaryChart: !index,
          hideSizeChart: !!hideSizeChart,
        };
      });
    };

    const initModalScrollHandlers = ({ section }) => {
      const dialog = section.querySelector(selectors.dialog);
      const trigger = section.querySelector(selectors.trigger);
      
      if (!dialog || !trigger) return;

      trigger.addEventListener('click', () => {
        disableBodyScroll();
      });

      dialog.addEventListener('close', () => {
        enableBodyScroll();
      });

      dialog.addEventListener('cancel', () => {
        enableBodyScroll();
      });
    };

    const initRevealHandlers = ({ section }) => {
      const mode = section.dataset.mode;
      if (mode !== 'simple' && mode !== 'complex') return;

      const container = section.querySelector(selectors.chartContainer);
      if (!container) return;

      // Tap toggles the reveal only on mobile widths; desktop reveals on hover via CSS.
      // Checked at click time (width-based, not pointer) so it survives resize / DevTools mobile.
      const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

      // Clears manual reveals but keeps the calculator result (is-matched) highlighted.
      const closeAll = (except) =>
        container
          .querySelectorAll('.is-revealed')
          .forEach((el) => el !== except && !el.classList.contains('is-matched') && el.classList.remove('is-revealed'));

      // Deselect (tap outside / on a non-cell): clear the cell AND revert the summary block so they
      // stay in sync — back to the calculator result if there was one, otherwise the default helper.
      const restore = () => {
        if (mode === 'complex' && section.__sgHighlight) {
          section.__sgHighlight(section.__sgMatched);
          if (section.__sgRenderSummary) section.__sgRenderSummary(section.__sgMatched);
        } else {
          closeAll();
        }
      };

      // Delegated: the table re-renders on chart/unit switch, so listen on the stable container.
      // Complex reveals on click at any width (design: "click ... click again to clear"); simple
      // keeps the mobile-only tap toggle (desktop simple has no reveal cells anyway).
      container.addEventListener('click', (event) => {
        if (!isMobile() && mode !== 'complex') return;
        const cell = event.target.closest('td[data-size]');
        if (!cell) return restore();
        if (cell.classList.contains('is-matched')) return; // never touch the calculator's result cell
        const willOpen = !cell.classList.contains('is-revealed');
        closeAll(cell);
        cell.classList.toggle('is-revealed', willOpen);
        // If the calculator already found a size, its block stays and a manual pick just reveals the
        // cell (no block change). If the shopper came straight to the chart (no calc result), the
        // manual pick drives the block.
        const hasCalcResult = section.__sgMatched && section.__sgMatched.length;
        if (mode === 'complex' && section.__sgRenderSummary && !hasCalcResult) {
          if (willOpen) section.__sgRenderSummary(String(cell.dataset.size).split('/'), 'manual');
          else section.__sgRenderSummary(section.__sgMatched); // cleared → default helper
        }
      });

      // ponytail: tap outside closes; scroll-to-close skipped until QA asks for it
      // Self-removing: the theme editor re-inits on every shopify:section:load with a fresh DOM,
      // so a listener whose container is gone unsubscribes itself instead of stacking up.
      const controller = new AbortController();
      document.addEventListener('click', (event) => {
        if (!container.isConnected) return controller.abort();
        if (!container.contains(event.target)) restore();
      }, { signal: controller.signal });
    };

    const selectors = {
      section: '.size-guide',
      chartsJson: '[data-size-charts-json]',
      intro: '.size-guide__intro',
      additionalChartsJson: '[data-additional-size-guides-json]',
      chartContainer: '.size-guide__chart-container',
      additionalChartContainer: '.size-guide__chart-info-container',
      chartPlaceholder: '.size-guide__chart-placeholder',
      tableWrapper: '.size-guide__table-outer',
      tableInner: '.size-guide__table-inner',
      chartSwitch: '.size-guide__switch--chart',
      unitSwitch: '.size-guide__switch--unit',
      calculatorForm: '#size-calculator',
      calculatorFormField: '.size-guide__popup-calculator-field',
      calculatorResult: '.size-guide__popup-calculator-result',
      calculatorResultButton: '.size-guide__popup-calculator-result-button',
      popupTabs: '.size-guide__popup-tabs',
      popupTabContent: '.size-guide__popup-tab-content',
      inputChecked: 'input:checked',
      dialog: 'dialog',
      trigger: '.product-section__size-guide-trigger',
    };

    const sections = document.querySelectorAll(selectors.section);
    sections.forEach((section, index) => {
      const charts = getCharts({ section });
      const [additionalGuides, titles] = getAdditionalGuides({ section });
      
      if (!charts || charts.length === 0) {
        return;
      }
      
      const filteredCharts = charts.filter((chart) => !chart.hideSizeChart);

      initChartHandlers({ section, charts: filteredCharts, additionalGuides, titles, sectionIndex: index });
      initRevealHandlers({ section });

      if (section.dataset.type === 'popup') {
        initPopupHandlers({ section });
        initStepHandlers({ section });
        initCalculatorHandlers({ section, charts: filteredCharts });
        initModalScrollHandlers({ section });
      }
    });


    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector(selectors.section);
      if (!section) return;

      const charts = getCharts({ section });
      const [additionalGuides, titles] = getAdditionalGuides({ section });
      if (!charts || charts.length === 0) return;
      const filteredCharts = charts.filter((chart) => !chart.hideSizeChart);

      initChartHandlers({ section, charts: filteredCharts, additionalGuides, titles });
      initRevealHandlers({ section });
    });
  };

  window.addEventListener('DOMContentLoaded', callback);
})();